import { ReactElement, ReactNode, RefObject, useCallback, useRef, useState } from 'react';
import { DownloadButton, useDownloadHandler } from './ui';
import { Perm, useHasPerm, useHasPerms } from 'core/permissions';
import { stringify } from 'qs';
import { useModel } from './scenes';
import {
  Button,
  DialogBody,
  DialogClose,
  DialogFooter,
  DialogHeader,
  PopoverRef,
  RenderProps,
  openModal,
} from '@ff-it/ui';
import { SubmissionErrors } from 'final-form';
import { FileField, Form, Submit, getDRFFormError } from '@ff-it/form';
import { checkActionError, required } from 'utilities';
import { fetcher } from 'services';
import { toast } from 'react-toastify';
import { useTable } from './ControlledTable';
import { Action, ActionDropdown } from './Actions';

interface ListExportProps {
  permission?: Perm;
  filter?: Record<string, unknown>;
  endpoint?: string;
  children?: ReactNode;
}

export function ListExport({
  permission,
  endpoint: providedEndpoint,
  filter: extraFilter,
  children = 'Export',
}: ListExportProps): ReactElement | null {
  const { endpoint } = useModel();
  const { filter, queryParams } = useTable();
  const [canExport] = useHasPerms(permission);
  if (!canExport) {
    return null;
  }

  const url = providedEndpoint || `${endpoint}export/`;

  return (
    <DownloadButton
      url={`${url}?${stringify({ ...extraFilter, ...queryParams, ...filter }, { indices: false })}`}
      icon="file-excel"
      title="Export"
      size="sm"
      variant="secondary"
      className="mr-1"
    >
      {children}
    </DownloadButton>
  );
}

type ImportSubmitHandler = (values: any) => Promise<SubmissionErrors | void>;

export function ImportDialog({
  onSubmit,
  submitHandler,
  children,
}: RenderProps & {
  submitHandler: ImportSubmitHandler;
  children?: ReactNode;
}): ReactElement {
  return (
    <Form
      autoComplete="off"
      onSubmit={(v) =>
        submitHandler(v).then((v) => {
          if (!v) {
            onSubmit();
          }

          return v;
        })
      }
    >
      <DialogHeader title="Import" />
      <DialogBody>
        <FileField name="file" label="File" required validate={required} />
        {children}
      </DialogBody>
      <DialogFooter>
        <DialogClose />
        <Submit ml="auto">Import</Submit>
      </DialogFooter>
    </Form>
  );
}

function useImportSubmitHandler(endpoint: string): ImportSubmitHandler {
  const [err, setError] = useState();
  if (err) {
    throw err;
  }
  return useCallback(
    async (values) => {
      const fd = new FormData();
      for (const [key, value] of Object.entries(values)) {
        if (value) {
          fd.append(key, value as any);
        }
        try {
          const res = await fetcher<{ message: string }>({
            url: endpoint,
            method: 'POST',
            body: fd,
          });
          toast.success(res.message, { autoClose: 3000 });
        } catch (error: any) {
          const formError = getDRFFormError(error);
          if (formError) {
            return formError;
          }
          if (!checkActionError(error)) {
            setError(error);
          }
        }
      }
    },
    [endpoint],
  );
}

interface ListImportProps {
  permission?: Perm;
  endpoint?: string;
  children?: ReactNode;
  dialogChildren?: ReactNode;
}

export function ListImport({
  permission,
  endpoint: providedEndpoint,
  children = 'Import',
  dialogChildren,
}: ListImportProps): ReactElement | null {
  const { endpoint } = useModel();
  const url = providedEndpoint || `${endpoint}import/`;

  const submitHandler = useImportSubmitHandler(url);
  const [canImport] = useHasPerms(permission);
  if (!canImport) {
    return null;
  }

  const clickHandler = (): any =>
    openModal((props) => (
      <ImportDialog {...props} submitHandler={submitHandler}>
        {dialogChildren}
      </ImportDialog>
    ));

  return (
    <Button title="Import" size="sm" variant="secondary" className="mr-1" onClick={clickHandler} icon="file-excel">
      {children}
    </Button>
  );
}

type ImportExportActionsProps = {
  import: ListImportProps;
  export: ListExportProps;
};

function useExportAction(
  { permission, endpoint: providedEndpoint, filter: extraFilter, children = 'Export' }: ListExportProps,
  ref: RefObject<PopoverRef>,
): Action | undefined {
  const { endpoint } = useModel();
  const { filter, queryParams } = useTable();
  const canExport = useHasPerm(permission);
  const url = providedEndpoint || `${endpoint}export/`;
  const [loading, setLoading] = useState(false);
  const onDownload = useDownloadHandler(
    {
      url: `${url}?${stringify({ ...extraFilter, ...queryParams, ...filter }, { indices: false })}`,
      queryParams,
    },
    setLoading,
  );
  const onClick = async (event: any) => {
    if (loading) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    await onDownload();
    ref.current?.setIsOpen(false);
  };

  if (!canExport) {
    return undefined;
  }

  return {
    action: 'export',
    button: {
      children,
      icon: loading ? 'loader' : 'file-arrow-down',
      onClick,
    },
  };
}

function useImportAction({
  permission,
  endpoint: providedEndpoint,
  children = 'Import',
  dialogChildren,
}: ListImportProps): Action | undefined {
  const { endpoint } = useModel();
  const url = providedEndpoint || `${endpoint}import/`;

  const submitHandler = useImportSubmitHandler(url);
  const [canImport] = useHasPerms(permission);
  if (!canImport) {
    return undefined;
  }

  const onClick = (): any =>
    openModal((props) => (
      <ImportDialog {...props} submitHandler={submitHandler}>
        {dialogChildren}
      </ImportDialog>
    ));

  return {
    action: 'import',
    button: {
      children,
      icon: 'file-arrow-up',
      onClick,
    },
  };
}

export function ImportExportActions(props: ImportExportActionsProps): ReactElement {
  const popoverRef = useRef(null);

  const actions: Action[] = [];
  const exportAction = useExportAction(props.export, popoverRef);
  if (exportAction) {
    actions.push(exportAction);
  }
  const importAction = useImportAction(props.import);
  if (importAction) {
    actions.push(importAction);
  }

  return (
    <ActionDropdown
      actions={actions}
      variant="secondary"
      icon="file-xls"
      testId="import-export-actions"
      size="sm"
      className="mr-1"
      popoverRef={popoverRef}
    >
      Import / Export
    </ActionDropdown>
  );
}
